import { useCallback, useContext, useMemo } from 'react';
import { MdAdd, MdAddToPhotos } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton/IconButton';
import { BPMTable, TypedHeader } from '@Components/StickyTable/BPMTable';
import { Appearance, Size } from '@Components/designConstants';
import { AD_SERVER_MAP } from '@Components/CreativeAllocation';
import { useCreativeMap } from '../../redux/creative';
import { useSetError } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { NonCartContext } from './noncart-context';
import { Placement, StreamingCreativesContext } from './streaming-creatives-constants';

// Filter-only fields (not table columns). optionsGetter is consumed by the shared
// boolean-logic filter parser to surface per-placement option values, matching bpm.
interface FilterFieldOption {
  name: string;
  label: string;
  optionsGetter?: (placement: Placement) => string[];
}

const renderNoPlacements = () => <div>No Placements</div>;

const displayPlacementType = (flashtalkingPlacementType?: string): string => {
  if (!flashtalkingPlacementType) {
    return 'N/A';
  }
  switch (flashtalkingPlacementType) {
    case 'Multi-tracker':
      return 'Pixel';
    case 'VAST':
      return 'Video VAST';
    case 'Audio':
      return 'Audio VAST';
    default:
      return flashtalkingPlacementType;
  }
};

export const Menu = () => {
  const { cid: company } = useCompanyInfo();
  const { creativeMap } = useCreativeMap({
    company,
    mediaTypes: ['streaming', 'audio', 'display']
  });
  const {
    shopData,
    selectedMap,
    setSelectedMap,
    inCart,
    setSelectedValue,
    platform,
    setPlatform,
    adServer,
    dimensions,
    setAdServer,
    setDimensions,
    length,
    setLength,
    flashtalkingPlacementType,
    setFlashtalkingPlacementType
  } = useContext(StreamingCreativesContext);
  const { setViewPlacementId, filteredPlacements, setFilteredPlacements } =
    useContext(NonCartContext);

  const setError = useSetError();

  const filteredLines = useMemo(
    () => shopData.placements.filter(({ id }) => !(selectedMap[id] || inCart[id])),
    [shopData, selectedMap, inCart]
  );

  const headers = useMemo<TypedHeader<Placement>[]>(
    () => [
      {
        label: '',
        name: 'id',
        width: 63,
        nonInteractive: true,
        renderer: ({ id }) => (
          <IconButton
            data-testid={`add-placement-${id}`}
            appearance={Appearance.OUTLINED}
            size={Size.SM}
            onClick={() => {
              const target = shopData.placementMap[id];
              if (adServer && platform && Object.values(selectedMap)) {
                if (
                  target.platform.split(' ')[0] === platform &&
                  target.adServer === adServer &&
                  (!dimensions || target.dimensions === dimensions) &&
                  (!length || target.length === `${length}s`) &&
                  (!flashtalkingPlacementType ||
                    target.flashtalkingPlacementType === flashtalkingPlacementType)
                ) {
                  setSelectedValue(id, true);
                } else if (adServer !== target.adServer) {
                  setError({
                    message: `You are currently editing ${AD_SERVER_MAP[adServer]} placements. Please commit these changes before selecting placements for ${target.adServer}.`
                  });
                } else if (dimensions && dimensions !== target.dimensions) {
                  setError({
                    message: `You are currently editing placements with dimensions ${dimensions}. Please commit these changes before selecting placements with different dimensions.`
                  });
                } else if (
                  flashtalkingPlacementType &&
                  flashtalkingPlacementType !== target.flashtalkingPlacementType
                ) {
                  setError({
                    message: `You are currently editing ${displayPlacementType(
                      flashtalkingPlacementType
                    )} placements. Please commit these changes before selecting placements for ${displayPlacementType(
                      target.flashtalkingPlacementType
                    )}`
                  });
                } else if (length && `${length}s` !== target.length) {
                  setError({
                    message: `You are currently editing placements with length ${length}. Please commit these changes before selecting placements with different lengths.`
                  });
                } else {
                  setError({
                    message: `You are currently editing ${platform} placements. Please commit these changes before selecting placements for other platforms.`
                  });
                }
              } else {
                const newPlatform = target.platform.split(' ')[0];
                setPlatform(newPlatform);
                setAdServer(target.adServer);
                setSelectedValue(id, true);
                setDimensions(
                  newPlatform.includes('Display')
                    ? (target.rotationKey.match(/(\d+x\d+)/)?.[1] ?? '')
                    : ''
                );
                if (target.length) {
                  setLength(target.length.replace(/s/g, ''));
                }
                if (target.flashtalkingPlacementType) {
                  setFlashtalkingPlacementType(target.flashtalkingPlacementType);
                }
              }
            }}
          >
            <MdAdd />
          </IconButton>
        )
      },
      { label: 'Network', name: 'network', flex: 1 },
      { label: 'Network Group', name: 'network_group', flex: 1, minFlexWidth: 140 },
      { label: 'Platform', name: 'platform', flex: 1, minFlexWidth: 140 },
      { label: 'Length', name: 'length', flex: 1, minFlexWidth: 140 },
      { label: 'Tag Name', name: 'name', flex: 3 },
      {
        label: 'Campaign',
        name: 'campaign',
        flex: 1,
        renderer: row => row.campaign || 'N/A'
      },
      {
        label: 'Ad Server',
        name: 'adServer',
        flex: 1,
        renderer: row => (row.adServer ? 'Flashtalking' : 'N/A')
      },
      {
        label: 'FT Placement Type',
        name: 'flashtalking_placement_type',
        flex: 1,
        renderer: row => displayPlacementType(row.flashtalkingPlacementType)
      },
      {
        label: '',
        name: 'id',
        width: 160,
        nonInteractive: true,
        renderer: ({ id }) => (
          <Button
            appearance={Appearance.EMPTY}
            size={Size.SM}
            onClick={() => setViewPlacementId(id)}
          >
            View Placement
          </Button>
        )
      }
    ],
    [
      adServer,
      dimensions,
      flashtalkingPlacementType,
      length,
      platform,
      selectedMap,
      setAdServer,
      setDimensions,
      setError,
      setFlashtalkingPlacementType,
      setLength,
      setPlatform,
      setSelectedValue,
      setViewPlacementId,
      shopData
    ]
  );

  const additionalFilterOptions = useMemo<FilterFieldOption[]>(
    () => [
      { label: 'Start Date', name: 'placement_start' },
      { label: 'End Date', name: 'placement_end' },
      {
        label: 'ISCI',
        name: 'isci',
        optionsGetter: placement => placement.creatives?.map(creative => creative.isci) ?? []
      },
      {
        label: 'Creative',
        name: 'creativeName',
        optionsGetter: placement =>
          placement.creatives
            ?.map(creative => creativeMap?.[creative.isci]?.name)
            .filter((name): name is string => Boolean(name)) ?? []
      }
    ],
    [creativeMap]
  );

  const addAllFiltered = useCallback(() => {
    const newMap = { ...selectedMap };
    for (const id of filteredPlacements) {
      newMap[id] = true;
    }
    setSelectedMap(newMap);
  }, [filteredPlacements, selectedMap, setSelectedMap]);

  const data = useMemo(() => filteredLines.filter(line => line.adServer === 'ft'), [filteredLines]);

  return (
    <BPMTable
      data={data}
      headers={headers}
      cellClassName='placement-table-cell'
      headerHeight={50}
      rowHeight={40}
      additionalControls={
        <Button
          appearance={Appearance.OUTLINED}
          size={Size.SM}
          leadingElement={<MdAddToPhotos />}
          onClick={addAllFiltered}
        >
          Add All Filtered
        </Button>
      }
      additionalFilterOptions={additionalFilterOptions}
      noRowsRenderer={renderNoPlacements}
      onFilteredDataChange={setFilteredPlacements}
    />
  );
};
